import React, { useEffect, useState } from "react";
import { Form, Input, Switch } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormPage } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";

/**
 * Full-page create/edit form for clients. On create, the API may return
 * one-time portal credentials — they are handed back to the list screen
 * via navigation state so CredentialsModal can show them exactly once.
 */
const ClientFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = location.state?.record ?? null;
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const { mutate } = useMutations();

  const createPortalUser = Form.useWatch("createPortalUser", form);

  useEffect(() => {
    if (isEdit && !editing) {
      navigate("/clients", { replace: true });
      return;
    }
    if (editing) {
      form.setFieldsValue(editing);
    } else {
      form.setFieldsValue({ createPortalUser: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = { ...values };
    setSubmitting(true);

    if (isEdit) {
      await mutate("updateClient", {
        method: "put",
        endpoint: `/clients/${id}`,
        payload,
        onSuccess: () => navigate("/clients"),
      });
    } else {
      await mutate("createClient", {
        method: "post",
        endpoint: "/clients",
        payload,
        onSuccess: (res) =>
          navigate("/clients", {
            state: {
              portalCredentials: res?.portalUser ?? null,
              portalWarning: res?.portalUserWarning ?? null,
            },
          }),
      });
    }

    setSubmitting(false);
  };

  return (
    <FormPage
      title={isEdit ? "Edit Client" : "Create Client"}
      subtitle={
        isEdit
          ? `Editing "${editing?.name ?? ""}".`
          : "Register a company and optionally issue a client-portal login."
      }
      backTo="/clients"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitText={isEdit ? "Save Changes" : "Create Client"}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="companyId" hidden>
          <Input />
        </Form.Item>

        <div className="form-grid">
          <div className="form-section-title">Company</div>

          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="organizationName" label="Organization Name">
            <Input />
          </Form.Item>
          <Form.Item name="fiscalNumber" label="Fiscal Number">
            <Input />
          </Form.Item>
          <Form.Item name="contactPerson" label="Contact Person">
            <Input />
          </Form.Item>

          <div className="form-section-title">Contact</div>

          <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="billingEmail" label="Billing Email" rules={[{ type: "email" }]}>
            <Input />
          </Form.Item>

          <div className="form-section-title">Address</div>

          <Form.Item name="address" label="Address" className="form-grid__full">
            <Input />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item name="postalCode" label="Postal Code">
            <Input />
          </Form.Item>
          <Form.Item name="country" label="Country">
            <Input />
          </Form.Item>

          {!isEdit && (
            <>
              <div className="form-section-title">Client Portal</div>

              <Form.Item
                name="createPortalUser"
                label="Create a client portal login"
                valuePropName="checked"
                className="form-grid__full"
                extra="Generates a temporary password, emails it to the client, and forces a change at first sign-in."
              >
                <Switch />
              </Form.Item>
              {createPortalUser && (
                <>
                  <Form.Item
                    name="portalUsername"
                    label="Portal username"
                    extra="Leave empty to derive it from the client name."
                  >
                    <Input placeholder="optional" />
                  </Form.Item>
                  <Form.Item
                    name="portalEmail"
                    label="Send credentials to"
                    rules={[{ type: "email" }]}
                    extra="Defaults to the client email above."
                  >
                    <Input placeholder="optional" />
                  </Form.Item>
                </>
              )}
            </>
          )}
        </div>
      </Form>
    </FormPage>
  );
};

export default ClientFormPage;
